import { CHANNELS } from "../shared/channels";
import { ReferenceEntry } from "../shared/types";
import { call, listen } from "./bridge";

// one copy of the ref list for the board, chat cards and gesture drawing.
// an update that carries its ref is patched in place; anything else reloads the list,
// at most once every few seconds, and not at all while the app is in the background

const MIN_GAP_MS = 3000;

type RefsUpdate = { id?: string; entry?: ReferenceEntry; removed?: boolean; progress?: boolean };

let entries: ReferenceEntry[] = [];
let loaded = false;
let inflight: Promise<ReferenceEntry[]> | null = null;
let timer = 0;
let lastFetch = 0;
let stale = false;
let paintQueued = false;
const subscribers = new Set<(list: ReferenceEntry[]) => void>();

export function refsNow(): ReferenceEntry[] {
  return entries;
}

export function refsLoaded(): boolean {
  return loaded;
}

export function loadRefs(force = false): Promise<ReferenceEntry[]> {
  if (loaded && !force) return Promise.resolve(entries);
  inflight ??= (() => {
    lastFetch = Date.now();
    stale = false;
    return call<ReferenceEntry[]>(CHANNELS.referencesList)
      .then((list) => {
        entries = list;
        loaded = true;
        subscribers.forEach((listener) => listener(entries));
        return entries;
      })
      .finally(() => {
        inflight = null;
      });
  })();
  return inflight;
}

export function refreshRefsSoon(delayMs = 450): void {
  if (document.visibilityState === "hidden") {
    stale = true;
    return;
  }
  // a reload is already on its way and will pick this change up
  if (timer) return;
  const wait = Math.max(delayMs, lastFetch + MIN_GAP_MS - Date.now());
  timer = window.setTimeout(() => {
    timer = 0;
    void loadRefs(true).catch(() => undefined);
  }, wait);
}

export function onRefs(listener: (list: ReferenceEntry[]) => void): void {
  subscribers.add(listener);
}

// repaint from the copy we have, after the other listeners for the same event have run
function paintSoon(): void {
  if (paintQueued) return;
  paintQueued = true;
  window.setTimeout(() => {
    paintQueued = false;
    subscribers.forEach((listener) => listener(entries));
  }, 0);
}

function patch(update: RefsUpdate): boolean {
  if (update.removed && update.id) {
    entries = entries.filter((entry) => entry.id !== update.id);
    return true;
  }
  const fresh = update.entry;
  if (!fresh?.id) return false;
  const at = entries.findIndex((entry) => entry.id === fresh.id);
  entries = at >= 0 ? entries.map((entry, index) => (index === at ? fresh : entry)) : [fresh, ...entries];
  return true;
}

export function setupRefsStore(): void {
  listen<RefsUpdate>(CHANNELS.referencesUpdated, (payload) => {
    if (!loaded) return;
    if (payload?.progress) return paintSoon();
    if (payload && patch(payload)) return paintSoon();
    refreshRefsSoon();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && loaded && stale) refreshRefsSoon(0);
  });
}
